<template>
  <v-card>
    <v-card-title>Services</v-card-title>
    <v-card-text>
      <v-fade-transition>
        <v-alert
          v-show="actionAlert"
          border="left"
          color="success"
          dark
          text
          dismissible
        >
          {{ actionAlertMessage }}
        </v-alert>
      </v-fade-transition>
    </v-card-text>
    <v-card-text class="d-flex align-center flex-wrap pb-0">
      <!-- search -->
      <v-text-field
        v-model="searchQuery"
        placeholder="Search"
        outlined
        hide-details
        dense
        clearable
        class="user-search me-3 mb-4"
        @keyup.enter="fetchServices"
        @click:clear="clearSearch"
      >
      </v-text-field>

      <v-spacer></v-spacer>

      <div class="d-flex align-center flex-wrap">
        <v-btn
          v-if=" $store.getters['auth/can'](['create services'])"
          color="primary"
          class="mb-4 me-3"
          @click="
            serviceData = {}
            servicesEditorDialog = !servicesEditorDialog
          "
        >
          <v-icon>{{ icons.mdiPlus }}</v-icon>
          <span>Add New Service</span>
        </v-btn>

        <!-- <v-btn
          color="secondary"
          outlined
          disabled
          class="mb-4"
        >
          <v-icon
            size="17"
            class="me-1"
          >
            {{ icons.mdiExportVariant }}
          </v-icon>
          <span>Export</span>
        </v-btn> -->
      </div>
    </v-card-text>
    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="services"
        :options.sync="options"
        :server-items-length="totalServices"
        :footer-props="footerProps"
        @page-count="pageCount = $event"
      >
        <!-- description -->
        <template #[`item.description`]="{ item }">
          <span class="d-block font-weight-semibold text-truncate text--primary">{{ item.description }}</span>
        </template>

        <!-- actions -->
        <template #[`item.actions`]="{ item }">
          <v-menu
            bottom
            left
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                v-if=" $store.getters['auth/can'](['edit services'])"
                @click="editItem(item)"
              >
                <v-list-item-title>
                  <v-icon
                    size="20"
                    class="me-2"
                  >
                    {{ icons.mdiPencilOutline }}
                  </v-icon>
                  <span>Edit</span>
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                v-if=" $store.getters['auth/can'](['delete services'])"
                @click="deleteItemConfirm(item)"
              >
                <v-list-item-title>
                  <v-icon
                    size="20"
                    class="me-2"
                  >
                    {{ icons.mdiDeleteOutline }}
                  </v-icon>
                  <span>Delete</span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card-text>
    <editor
      :services-editor-dialog.sync="servicesEditorDialog"
      :service-data="serviceData"
      @saveService="saveService"
    ></editor>
    <!-- dialog -->
    <v-dialog
      v-model="dialogDelete"
      max-width="500px"
    >
      <v-card>
        <v-card-title> Are you sure you want to delete this item? </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            outlined
            @click="dialogDelete = !dialogDelete"
          >
            Cancel
          </v-btn>
          <v-btn
            color="success"
            @click="deleteItem"
          >
            OK
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {
  mdiSquareEditOutline,
  mdiDotsVertical,
  mdiPlus,
  mdiExportVariant,
  mdiPencilOutline,
  mdiDeleteOutline,
} from '@mdi/js'
import { onMounted, ref, watch } from '@vue/composition-api'
import axios from '@axios'
import Editor from './Editor.vue'
import useServices from './useServices'
import useUIResolver from '../useUIResolver'

const defaultAlertMessage = 'Service successfully saved.'

export default {
  components: {
    Editor,
  },
  setup() {
    const serviceData = ref({})
    const actionAlert = ref(false)
    const actionAlertMessage = ref('')
    const servicesEditorDialog = ref(false)
    const searchQuery = ref('')
    const totalServices = ref(0)
    const footerProps = ref({
      itemsPerPageOptions: [15, 50, 100],
    })
    const options = ref({
      groupBy: [],
      groupDesc: [],
      itemsPerPage: 15,
      multiSort: false,
      mustSort: false,
      page: 1,
      sortBy: ['description'],
      sortDesc: [],
    })
    const dialogDelete = ref(false)

    onMounted(() => {
      actionAlertMessage.value = defaultAlertMessage
    })

    const { services, getServices } = useServices()
    const { serviceTableHeaders } = useUIResolver()

    const fetchServices = () => {
      getServices({ ...options.value, ...{ searchQuery: searchQuery.value } }).then(response => {
        const { total } = response.data
        totalServices.value = total
        options.value.itemsPerPage = response.data.per_page
      })
    }

    const clearSearch = () => {
      searchQuery.value = null
      fetchServices()
    }

    const saveService = () => {
      actionAlertMessage.value = defaultAlertMessage
      actionAlert.value = true
      fetchServices()
    }

    const editItem = item => {
      // show services-editor dialog
      servicesEditorDialog.value = true

      // assign the select service
      serviceData.value = item
    }

    const deleteItem = () => {
      axios.delete(`/services/${serviceData.value.id}`).then(response => {
        actionAlertMessage.value = 'Service successfully deleted.'
        actionAlert.value = true

        // hide confirm alert
        dialogDelete.value = false
        fetchServices()
      })
    }

    const deleteItemConfirm = item => {
      serviceData.value = item
      dialogDelete.value = true
    }

    watch(options, fetchServices)

    watch(actionAlert, val => {
      if (val) {
        setTimeout(() => {
          actionAlert.value = false
        }, 3000)
      }
    })

    return {
      dialogDelete,
      actionAlert,
      actionAlertMessage,
      serviceData,
      headers: serviceTableHeaders(),
      searchQuery,

      totalServices,
      footerProps,
      options,

      // useServices
      services,
      getServices,

      // functions
      clearSearch,
      servicesEditorDialog,
      saveService,
      fetchServices,

      editItem,
      deleteItem,
      deleteItemConfirm,

      // icons
      icons: {
        mdiSquareEditOutline,
        mdiDotsVertical,
        mdiPlus,
        mdiExportVariant,
        mdiPencilOutline,
        mdiDeleteOutline,
      },
    }
  },
}
</script>
